import React from 'react';
import { 
  Megaphone, Quote, Calendar, Users, Star, Plane, MapPin, Sparkles, 
  HelpCircle, Gift, Camera, MessageCircle, TrendingUp, Heart, 
  Coffee, Zap, Award, CloudSun, Layers
} from 'lucide-react';
import { useClient } from '../../context/ClientContext';

// Use CSS variable values to stay in sync with the active client branding
const FALLBACK = { primary: '#4A5568', secondary: '#718096', accent: '#2D3748' };

function getColors(client) {
  // Read from the client object, falling back to neutral grays (not CHO-specific)
  return {
    primary: client?.primary_color || FALLBACK.primary,
    secondary: client?.secondary_color || FALLBACK.secondary,
    accent: client?.accent_color || FALLBACK.accent,
  };
}

function getLogo(resolvedLogoUrl) {
  if (resolvedLogoUrl) return resolvedLogoUrl;
  return null; // No fallback to a specific client logo
}

// Helper: logo element
const logo = (src, x, y, w = 220, h = 80) => ({
  id: 'client-logo', type: 'image', src, x, y, width: w, height: h
});

const WHITE = '#ffffff';

function buildTemplates(colors, logoUrl) {
  const { primary, secondary, accent } = colors;
  // Darken primary for subtle text
  const muted = '#94a3b8';
  const dark = '#0f172a';

  return [
    // ───── ANNOUNCEMENT ─────
    {
      id: 'announcement',
      name: 'Announcement',
      icon: Megaphone,
      category: 'essential',
      preview: { bg: primary, accent: secondary },
      canvas: {
        width: 1080, height: 1080,
        backgroundColor: primary,
        elements: [
          { id: 'accent-top', type: 'rect', x: 0, y: 0, width: 1080, height: 8, fill: secondary },
          { id: 'accent-bottom', type: 'rect', x: 0, y: 1072, width: 1080, height: 8, fill: secondary },
          logo(logoUrl, 60, 50, 200, 72),
          { id: 'heading', type: 'text', text: 'YOUR\nANNOUNCEMENT\nHERE', x: 60, y: 280, fontSize: 80, fill: WHITE, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 960, align: 'left' },
          { id: 'subtext', type: 'text', text: 'Add details about your announcement below', x: 60, y: 680, fontSize: 28, fill: muted, fontFamily: "'Space Grotesk', sans-serif", width: 800, align: 'left' },
          { id: 'cta', type: 'rect', x: 60, y: 830, width: 300, height: 60, fill: secondary, cornerRadius: 12 },
          { id: 'cta-text', type: 'text', text: 'Learn More', x: 60, y: 842, fontSize: 28, fill: primary, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 300, align: 'center' },
        ]
      }
    },

    // ───── QUOTE CARD ─────
    {
      id: 'quote-card',
      name: 'Quote Card',
      icon: Quote,
      category: 'essential',
      preview: { bg: '#f0f4f8', accent: accent },
      canvas: {
        width: 1080, height: 1080,
        backgroundColor: '#f0f4f8',
        elements: [
          { id: 'accent-bar', type: 'rect', x: 60, y: 200, width: 8, height: 400, fill: accent, cornerRadius: 4 },
          { id: 'quote', type: 'text', text: '"Your inspiring quote goes here. Make it memorable."', x: 100, y: 280, fontSize: 52, fill: primary, fontFamily: "Georgia, serif", fontStyle: 'italic', width: 880, align: 'left' },
          { id: 'attribution', type: 'text', text: '— Speaker Name', x: 100, y: 640, fontSize: 28, fill: '#64748b', fontFamily: "'Space Grotesk', sans-serif", width: 500, align: 'left' },
          logo(logoUrl, 380, 860, 320, 115),
        ]
      }
    },

    // ───── EVENT PROMO ─────
    {
      id: 'event-promo',
      name: 'Event Promo',
      icon: Calendar,
      category: 'essential',
      preview: { bg: accent, accent: WHITE },
      canvas: {
        width: 1080, height: 1080,
        backgroundColor: accent,
        elements: [
          { id: 'date-box', type: 'rect', x: 60, y: 60, width: 200, height: 200, fill: WHITE, cornerRadius: 24 },
          { id: 'date-month', type: 'text', text: 'APR', x: 60, y: 80, fontSize: 28, fill: accent, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 200, align: 'center' },
          { id: 'date-day', type: 'text', text: '15', x: 60, y: 120, fontSize: 80, fill: primary, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 200, align: 'center' },
          { id: 'event-title', type: 'text', text: 'EVENT NAME', x: 60, y: 380, fontSize: 72, fill: WHITE, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 960, align: 'left' },
          { id: 'event-details', type: 'text', text: 'Time, location, and other details here', x: 60, y: 550, fontSize: 28, fill: WHITE + '99', fontFamily: "'Space Grotesk', sans-serif", width: 800, align: 'left' },
          { id: 'divider', type: 'rect', x: 60, y: 700, width: 200, height: 4, fill: secondary },
          { id: 'extra-info', type: 'text', text: 'Free admission  |  Open to public', x: 60, y: 740, fontSize: 24, fill: WHITE + '99', fontFamily: "'Space Grotesk', sans-serif", width: 800, align: 'left' },
          logo(logoUrl, 60, 930, 240, 87),
        ]
      }
    },

    // ───── TRAVEL TIP ─────
    {
      id: 'travel-tip',
      name: 'Travel Tip',
      icon: Plane,
      category: 'content',
      preview: { bg: dark, accent: secondary },
      canvas: {
        width: 1080, height: 1080,
        backgroundColor: dark,
        elements: [
          { id: 'accent-top', type: 'rect', x: 0, y: 0, width: 1080, height: 6, fill: secondary },
          { id: 'tag', type: 'rect', x: 60, y: 80, width: 180, height: 40, fill: secondary, cornerRadius: 20 },
          { id: 'tag-text', type: 'text', text: 'TRAVEL TIP', x: 60, y: 86, fontSize: 18, fill: dark, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 180, align: 'center' },
          { id: 'tip-number', type: 'text', text: '#01', x: 60, y: 200, fontSize: 120, fill: '#1e293b', fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 400, align: 'left' },
          { id: 'heading', type: 'text', text: 'Your Travel Tip\nTitle Here', x: 60, y: 400, fontSize: 64, fill: WHITE, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 960, align: 'left' },
          { id: 'body', type: 'text', text: 'Share helpful travel advice. Keep it concise and actionable.', x: 60, y: 620, fontSize: 26, fill: muted, fontFamily: "'Space Grotesk', sans-serif", width: 800, align: 'left' },
          logo(logoUrl, 60, 920, 220, 80),
        ]
      }
    },

    // ───── STAFF SPOTLIGHT ─────
    {
      id: 'staff-spotlight',
      name: 'Staff Spotlight',
      icon: Users,
      category: 'content',
      preview: { bg: WHITE, accent: primary },
      canvas: {
        width: 1080, height: 1080,
        backgroundColor: WHITE,
        elements: [
          { id: 'header-bg', type: 'rect', x: 0, y: 0, width: 1080, height: 120, fill: primary },
          { id: 'header-text', type: 'text', text: 'STAFF SPOTLIGHT', x: 60, y: 36, fontSize: 32, fill: secondary, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 400, align: 'left' },
          logo(logoUrl, 780, 25, 240, 70),
          { id: 'photo-placeholder', type: 'rect', x: 60, y: 180, width: 960, height: 500, fill: '#f1f5f9', cornerRadius: 20 },
          { id: 'photo-label', type: 'text', text: 'Drop photo here', x: 60, y: 380, fontSize: 28, fill: '#cbd5e1', fontFamily: "'Space Grotesk', sans-serif", width: 960, align: 'center' },
          { id: 'name', type: 'text', text: 'Employee Name', x: 60, y: 730, fontSize: 48, fill: primary, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 960, align: 'left' },
          { id: 'role', type: 'text', text: 'Job Title  |  Department', x: 60, y: 800, fontSize: 24, fill: accent, fontFamily: "'Space Grotesk', sans-serif", width: 600, align: 'left' },
          { id: 'quote', type: 'text', text: '"A short quote from the employee about what they love."', x: 60, y: 870, fontSize: 22, fill: '#64748b', fontFamily: "Georgia, serif", fontStyle: 'italic', width: 900, align: 'left' },
          { id: 'footer-bar', type: 'rect', x: 0, y: 1020, width: 1080, height: 60, fill: primary },
        ]
      }
    },

    // ───── TESTIMONIAL ─────
    {
      id: 'testimonial',
      name: 'Testimonial',
      icon: Star,
      category: 'essential',
      preview: { bg: secondary, accent: primary },
      canvas: {
        width: 1080, height: 1080,
        backgroundColor: secondary,
        elements: [
          { id: 'stars', type: 'text', text: '★ ★ ★ ★ ★', x: 140, y: 180, fontSize: 48, fill: primary, fontFamily: "'Space Grotesk', sans-serif", width: 800, align: 'center' },
          { id: 'testimonial', type: 'text', text: '"This was the best experience we\'ve ever had. Friendly staff, easy process, no stress!"', x: 100, y: 310, fontSize: 42, fill: primary, fontFamily: "Georgia, serif", fontStyle: 'italic', width: 880, align: 'center' },
          { id: 'author', type: 'text', text: '— Customer Name', x: 140, y: 660, fontSize: 24, fill: primary + '99', fontFamily: "'Space Grotesk', sans-serif", width: 800, align: 'center' },
          { id: 'divider', type: 'rect', x: 440, y: 780, width: 200, height: 3, fill: primary },
          logo(logoUrl, 380, 850, 320, 115),
        ]
      }
    },

    // ───── DESTINATION SPOTLIGHT ─────
    {
      id: 'destination',
      name: 'Destination',
      icon: MapPin,
      category: 'content',
      preview: { bg: '#0c4a6e', accent: secondary },
      canvas: {
        width: 1080, height: 1080,
        backgroundColor: '#0c4a6e',
        elements: [
          { id: 'top-bar', type: 'rect', x: 0, y: 0, width: 1080, height: 6, fill: secondary },
          { id: 'tag', type: 'rect', x: 60, y: 60, width: 280, height: 44, fill: secondary, cornerRadius: 22 },
          { id: 'tag-text', type: 'text', text: 'FLY NONSTOP', x: 60, y: 68, fontSize: 16, fill: '#0c4a6e', fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 280, align: 'center' },
          { id: 'photo-area', type: 'rect', x: 60, y: 140, width: 960, height: 420, fill: '#0e7490', cornerRadius: 20 },
          { id: 'photo-hint', type: 'text', text: 'Add destination photo', x: 60, y: 310, fontSize: 28, fill: '#67e8f9', fontFamily: "'Space Grotesk', sans-serif", width: 960, align: 'center' },
          { id: 'city', type: 'text', text: 'DESTINATION\nCITY', x: 60, y: 620, fontSize: 72, fill: WHITE, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 960, align: 'left' },
          { id: 'details', type: 'text', text: 'Direct flights  |  Airline Partner', x: 60, y: 810, fontSize: 24, fill: '#7dd3fc', fontFamily: "'Space Grotesk', sans-serif", width: 800, align: 'left' },
          { id: 'cta', type: 'rect', x: 60, y: 890, width: 260, height: 54, fill: WHITE, cornerRadius: 27 },
          { id: 'cta-text', type: 'text', text: 'Book Now', x: 60, y: 904, fontSize: 22, fill: '#0c4a6e', fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 260, align: 'center' },
          logo(logoUrl, 60, 975, 200, 72),
        ]
      }
    },

    // ───── BEHIND THE SCENES ─────
    {
      id: 'behind-scenes',
      name: 'Behind Scenes',
      icon: Camera,
      category: 'content',
      preview: { bg: '#1e293b', accent: '#f59e0b' },
      canvas: {
        width: 1080, height: 1080,
        backgroundColor: '#1e293b',
        elements: [
          { id: 'film-top', type: 'rect', x: 0, y: 0, width: 1080, height: 80, fill: '#0f172a' },
          { id: 'film-bottom', type: 'rect', x: 0, y: 1000, width: 1080, height: 80, fill: '#0f172a' },
          { id: 'tag', type: 'rect', x: 60, y: 110, width: 280, height: 40, fill: '#f59e0b', cornerRadius: 20 },
          { id: 'tag-text', type: 'text', text: 'BEHIND THE SCENES', x: 60, y: 117, fontSize: 16, fill: '#1e293b', fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 280, align: 'center' },
          { id: 'photo-area', type: 'rect', x: 60, y: 190, width: 960, height: 480, fill: '#334155', cornerRadius: 16 },
          { id: 'photo-hint', type: 'text', text: 'Add your behind-the-scenes photo', x: 60, y: 390, fontSize: 24, fill: '#64748b', fontFamily: "'Space Grotesk', sans-serif", width: 960, align: 'center' },
          { id: 'caption', type: 'text', text: 'Take a peek at what goes on behind the scenes!', x: 60, y: 720, fontSize: 36, fill: WHITE, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 900, align: 'left' },
          { id: 'subcaption', type: 'text', text: 'Describe what is happening in this photo...', x: 60, y: 840, fontSize: 22, fill: muted, fontFamily: "'Space Grotesk', sans-serif", width: 800, align: 'left' },
          logo(logoUrl, 60, 920, 180, 65),
        ]
      }
    },

    // ───── ENGAGEMENT / POLL ─────
    {
      id: 'engagement-poll',
      name: 'This or That',
      icon: MessageCircle,
      category: 'engagement',
      preview: { bg: primary, accent: WHITE },
      canvas: {
        width: 1080, height: 1080,
        backgroundColor: primary,
        elements: [
          logo(logoUrl, 60, 50, 180, 65),
          { id: 'heading', type: 'text', text: 'THIS OR THAT?', x: 60, y: 200, fontSize: 56, fill: WHITE, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 960, align: 'center' },
          { id: 'option-a-bg', type: 'rect', x: 80, y: 350, width: 420, height: 400, fill: secondary, cornerRadius: 24 },
          { id: 'option-a', type: 'text', text: 'OPTION A', x: 80, y: 500, fontSize: 40, fill: WHITE, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 420, align: 'center' },
          { id: 'vs', type: 'text', text: 'VS', x: 460, y: 510, fontSize: 32, fill: WHITE + '60', fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 160, align: 'center' },
          { id: 'option-b-bg', type: 'rect', x: 580, y: 350, width: 420, height: 400, fill: accent, cornerRadius: 24 },
          { id: 'option-b', type: 'text', text: 'OPTION B', x: 580, y: 500, fontSize: 40, fill: WHITE, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 420, align: 'center' },
          { id: 'cta', type: 'text', text: 'Vote in the comments!', x: 60, y: 840, fontSize: 28, fill: WHITE + '80', fontFamily: "'Space Grotesk', sans-serif", width: 960, align: 'center' },
        ]
      }
    },

    // ───── DID YOU KNOW ─────
    {
      id: 'did-you-know',
      name: 'Did You Know',
      icon: Sparkles,
      category: 'content',
      preview: { bg: WHITE, accent: accent },
      canvas: {
        width: 1080, height: 1080,
        backgroundColor: WHITE,
        elements: [
          { id: 'side-bar', type: 'rect', x: 0, y: 0, width: 12, height: 1080, fill: secondary },
          { id: 'tag', type: 'text', text: 'DID YOU KNOW?', x: 80, y: 80, fontSize: 24, fill: secondary, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 400, align: 'left' },
          { id: 'fact', type: 'text', text: 'Write your fun fact\nor statistic here', x: 80, y: 200, fontSize: 64, fill: primary, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 920, align: 'left' },
          { id: 'detail', type: 'text', text: 'Add context or a supporting detail that makes this fact interesting and shareable.', x: 80, y: 520, fontSize: 26, fill: '#64748b', fontFamily: "'Space Grotesk', sans-serif", width: 800, align: 'left' },
          { id: 'accent-circle', type: 'rect', x: 820, y: 60, width: 200, height: 200, fill: secondary, cornerRadius: 100, opacity: 0.15 },
          logo(logoUrl, 80, 900, 280, 100),
        ]
      }
    },

    // ───── SEASONAL / HOLIDAY ─────
    {
      id: 'seasonal',
      name: 'Seasonal',
      icon: Gift,
      category: 'essential',
      preview: { bg: primary, accent: '#f59e0b' },
      canvas: {
        width: 1080, height: 1080,
        backgroundColor: primary,
        elements: [
          { id: 'glow1', type: 'rect', x: 340, y: -100, width: 400, height: 400, fill: accent, cornerRadius: 200, opacity: 0.3 },
          { id: 'glow2', type: 'rect', x: 600, y: 700, width: 500, height: 500, fill: secondary, cornerRadius: 250, opacity: 0.1 },
          { id: 'greeting', type: 'text', text: 'Happy\nHolidays!', x: 80, y: 250, fontSize: 96, fill: WHITE, fontFamily: "Georgia, serif", fontStyle: 'italic', width: 920, align: 'center' },
          { id: 'message', type: 'text', text: 'Wishing warmth and joy\nfrom all of us', x: 80, y: 560, fontSize: 28, fill: WHITE + 'bb', fontFamily: "'Space Grotesk', sans-serif", width: 920, align: 'center' },
          { id: 'divider', type: 'rect', x: 440, y: 730, width: 200, height: 3, fill: secondary },
          logo(logoUrl, 380, 820, 320, 115),
        ]
      }
    },

    // ───── FAQ / PRO TIP ─────
    {
      id: 'faq',
      name: 'FAQ',
      icon: HelpCircle,
      category: 'content',
      preview: { bg: '#f8fafc', accent: primary },
      canvas: {
        width: 1080, height: 1080,
        backgroundColor: '#f8fafc',
        elements: [
          { id: 'header-bg', type: 'rect', x: 0, y: 0, width: 1080, height: 200, fill: primary },
          logo(logoUrl, 60, 20, 180, 65),
          { id: 'header-label', type: 'text', text: 'FREQUENTLY ASKED', x: 60, y: 100, fontSize: 20, fill: secondary, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 400, align: 'left' },
          { id: 'header-title', type: 'text', text: 'QUESTION?', x: 60, y: 130, fontSize: 40, fill: WHITE, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 600, align: 'left' },
          { id: 'question', type: 'text', text: 'Write your question here?', x: 60, y: 300, fontSize: 40, fill: primary, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 960, align: 'left' },
          { id: 'answer', type: 'text', text: 'Write a clear, helpful answer here. Keep it concise and friendly.', x: 60, y: 500, fontSize: 26, fill: '#475569', fontFamily: "'Space Grotesk', sans-serif", width: 900, align: 'left' },
          { id: 'pro-tip-bg', type: 'rect', x: 60, y: 780, width: 960, height: 100, fill: secondary, cornerRadius: 16, opacity: 0.15 },
          { id: 'pro-tip', type: 'text', text: 'Pro Tip: Add a helpful bonus tip here!', x: 90, y: 810, fontSize: 22, fill: primary, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 900, align: 'left' },
        ]
      }
    },

    // ═══════ NEW TEMPLATES ═══════

    // ───── PHOTO + CAPTION OVERLAY ─────
    {
      id: 'photo-caption',
      name: 'Photo + Caption',
      icon: Camera,
      category: 'photo',
      preview: { bg: '#1e293b', accent: secondary },
      canvas: {
        width: 1080, height: 1080,
        backgroundColor: '#1e293b',
        elements: [
          { id: 'photo-area', type: 'rect', x: 0, y: 0, width: 1080, height: 1080, fill: '#334155' },
          { id: 'photo-hint', type: 'text', text: 'Upload your photo as background', x: 0, y: 480, fontSize: 28, fill: '#64748b', fontFamily: "'Space Grotesk', sans-serif", width: 1080, align: 'center' },
          { id: 'gradient-bar', type: 'rect', x: 0, y: 780, width: 1080, height: 300, fill: primary, opacity: 0.85 },
          { id: 'caption', type: 'text', text: 'Your caption goes here. Keep it short and punchy.', x: 60, y: 830, fontSize: 32, fill: WHITE, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 860, align: 'left' },
          { id: 'hashtags', type: 'text', text: '#YourBrand #YourHashtag', x: 60, y: 940, fontSize: 20, fill: WHITE + '80', fontFamily: "'Space Grotesk', sans-serif", width: 800, align: 'left' },
          logo(logoUrl, 880, 950, 140, 50),
        ]
      }
    },

    // ───── STAT / METRIC HIGHLIGHT ─────
    {
      id: 'stat-highlight',
      name: 'Stat Highlight',
      icon: TrendingUp,
      category: 'content',
      preview: { bg: primary, accent: secondary },
      canvas: {
        width: 1080, height: 1080,
        backgroundColor: primary,
        elements: [
          { id: 'big-number', type: 'text', text: '500K+', x: 60, y: 200, fontSize: 180, fill: secondary, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 960, align: 'center' },
          { id: 'label', type: 'text', text: 'CUSTOMERS SERVED', x: 60, y: 440, fontSize: 32, fill: WHITE + 'cc', fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 960, align: 'center' },
          { id: 'divider', type: 'rect', x: 440, y: 540, width: 200, height: 4, fill: secondary },
          { id: 'context', type: 'text', text: 'Add context about this impressive number here. Make it relatable and shareable.', x: 140, y: 600, fontSize: 26, fill: WHITE + '99', fontFamily: "'Space Grotesk', sans-serif", width: 800, align: 'center' },
          logo(logoUrl, 420, 880, 240, 87),
        ]
      }
    },

    // ───── THANK YOU / APPRECIATION ─────
    {
      id: 'thank-you',
      name: 'Thank You',
      icon: Heart,
      category: 'engagement',
      preview: { bg: secondary, accent: WHITE },
      canvas: {
        width: 1080, height: 1080,
        backgroundColor: secondary,
        elements: [
          { id: 'big-text', type: 'text', text: 'Thank\nYou!', x: 60, y: 200, fontSize: 120, fill: WHITE, fontFamily: "Georgia, serif", fontStyle: 'italic', width: 960, align: 'center' },
          { id: 'message', type: 'text', text: 'We appreciate your support, loyalty, and trust.\nHere\'s to many more great moments together.', x: 100, y: 560, fontSize: 28, fill: WHITE + 'dd', fontFamily: "'Space Grotesk', sans-serif", width: 880, align: 'center' },
          { id: 'divider', type: 'rect', x: 440, y: 740, width: 200, height: 3, fill: WHITE + '40' },
          logo(logoUrl, 380, 820, 320, 115),
        ]
      }
    },

    // ───── SIMPLE TEXT POST (Bold Statement) ─────
    {
      id: 'bold-statement',
      name: 'Bold Statement',
      icon: Zap,
      category: 'engagement',
      preview: { bg: dark, accent: secondary },
      canvas: {
        width: 1080, height: 1080,
        backgroundColor: dark,
        elements: [
          { id: 'accent-line', type: 'rect', x: 80, y: 380, width: 100, height: 6, fill: secondary },
          { id: 'statement', type: 'text', text: 'Write a bold,\nattention-grabbing\nstatement here.', x: 80, y: 420, fontSize: 64, fill: WHITE, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 920, align: 'left' },
          { id: 'subtext', type: 'text', text: 'Add a brief supporting line or call to action.', x: 80, y: 740, fontSize: 24, fill: muted, fontFamily: "'Space Grotesk', sans-serif", width: 800, align: 'left' },
          logo(logoUrl, 80, 920, 180, 65),
        ]
      }
    },

    // ───── SERIES / RECURRING (Travel Tuesday etc) ─────
    {
      id: 'series-header',
      name: 'Series Header',
      icon: Award,
      category: 'essential',
      preview: { bg: primary, accent: secondary },
      canvas: {
        width: 1080, height: 1080,
        backgroundColor: primary,
        elements: [
          { id: 'badge-bg', type: 'rect', x: 340, y: 100, width: 400, height: 400, fill: secondary, cornerRadius: 200 },
          { id: 'badge-icon', type: 'text', text: '✈', x: 340, y: 200, fontSize: 140, fill: WHITE, fontFamily: "'Space Grotesk', sans-serif", width: 400, align: 'center' },
          { id: 'series-name', type: 'text', text: 'TRAVEL\nTUESDAY', x: 60, y: 580, fontSize: 80, fill: WHITE, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 960, align: 'center' },
          { id: 'tagline', type: 'text', text: 'Tips, destinations & fun every week', x: 60, y: 800, fontSize: 26, fill: WHITE + '99', fontFamily: "'Space Grotesk', sans-serif", width: 960, align: 'center' },
          logo(logoUrl, 420, 920, 240, 87),
        ]
      }
    },

    // ───── WEEKEND UPDATE / WEATHER ─────
    {
      id: 'weekend-update',
      name: 'Weekend Update',
      icon: CloudSun,
      category: 'content',
      preview: { bg: accent, accent: WHITE },
      canvas: {
        width: 1080, height: 1080,
        backgroundColor: accent,
        elements: [
          { id: 'tag', type: 'rect', x: 60, y: 60, width: 260, height: 44, fill: WHITE, cornerRadius: 22 },
          { id: 'tag-text', type: 'text', text: 'WEEKEND UPDATE', x: 60, y: 68, fontSize: 16, fill: accent, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 260, align: 'center' },
          { id: 'heading', type: 'text', text: 'What to Know\nThis Weekend', x: 60, y: 250, fontSize: 64, fill: WHITE, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 960, align: 'left' },
          { id: 'bullet-1', type: 'text', text: '→  Update point one goes here', x: 60, y: 520, fontSize: 28, fill: WHITE + 'dd', fontFamily: "'Space Grotesk', sans-serif", width: 900, align: 'left' },
          { id: 'bullet-2', type: 'text', text: '→  Update point two goes here', x: 60, y: 580, fontSize: 28, fill: WHITE + 'dd', fontFamily: "'Space Grotesk', sans-serif", width: 900, align: 'left' },
          { id: 'bullet-3', type: 'text', text: '→  Update point three goes here', x: 60, y: 640, fontSize: 28, fill: WHITE + 'dd', fontFamily: "'Space Grotesk', sans-serif", width: 900, align: 'left' },
          { id: 'divider', type: 'rect', x: 60, y: 750, width: 200, height: 4, fill: secondary },
          { id: 'footer', type: 'text', text: 'Have a great weekend!', x: 60, y: 800, fontSize: 24, fill: WHITE + '80', fontFamily: "'Space Grotesk', sans-serif", width: 800, align: 'left' },
          logo(logoUrl, 60, 930, 200, 72),
        ]
      }
    },

    // ───── PHOTO STORY (1080x1920 vertical) ─────
    {
      id: 'story-promo',
      name: 'Story / Reel',
      icon: Layers,
      category: 'photo',
      preview: { bg: primary, accent: secondary },
      canvas: {
        width: 1080, height: 1920,
        backgroundColor: primary,
        elements: [
          logo(logoUrl, 60, 80, 200, 72),
          { id: 'photo-area', type: 'rect', x: 0, y: 200, width: 1080, height: 1000, fill: '#334155' },
          { id: 'photo-hint', type: 'text', text: 'Upload photo as background\nor add image here', x: 0, y: 640, fontSize: 28, fill: '#64748b', fontFamily: "'Space Grotesk', sans-serif", width: 1080, align: 'center' },
          { id: 'headline', type: 'text', text: 'Your Story\nHeadline', x: 80, y: 1280, fontSize: 72, fill: WHITE, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 920, align: 'left' },
          { id: 'body', type: 'text', text: 'Add a brief description or call to action here.', x: 80, y: 1520, fontSize: 28, fill: WHITE + '99', fontFamily: "'Space Grotesk', sans-serif", width: 800, align: 'left' },
          { id: 'swipe-cta', type: 'rect', x: 340, y: 1720, width: 400, height: 60, fill: secondary, cornerRadius: 30 },
          { id: 'swipe-text', type: 'text', text: 'Swipe Up', x: 340, y: 1732, fontSize: 24, fill: WHITE, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 400, align: 'center' },
        ]
      }
    },

    // ───── ENGAGEMENT QUESTION ─────
    {
      id: 'question-post',
      name: 'Question Post',
      icon: Coffee,
      category: 'engagement',
      preview: { bg: '#fef3c7', accent: primary },
      canvas: {
        width: 1080, height: 1080,
        backgroundColor: '#fef3c7',
        elements: [
          { id: 'big-q', type: 'text', text: '?', x: 700, y: -40, fontSize: 400, fill: primary, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 400, align: 'center', opacity: 0.08 },
          { id: 'question', type: 'text', text: 'What\'s your\ngo-to travel\nsnack?', x: 80, y: 250, fontSize: 72, fill: primary, fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'bold', width: 920, align: 'left' },
          { id: 'cta', type: 'text', text: 'Tell us in the comments!', x: 80, y: 680, fontSize: 28, fill: primary + '99', fontFamily: "'Space Grotesk', sans-serif", width: 800, align: 'left' },
          { id: 'accent-line', type: 'rect', x: 80, y: 780, width: 120, height: 6, fill: secondary },
          logo(logoUrl, 80, 900, 220, 80),
        ]
      }
    },
  ];
}

// Template categories for filter tabs
const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'essential', label: 'Essential' },
  { id: 'content', label: 'Content' },
  { id: 'engagement', label: 'Engagement' },
  { id: 'photo', label: 'Photo' },
];

export function TemplateGallery({ onSelectTemplate }) {
  const { activeClient, resolvedLogoUrl } = useClient();
  const [activeCategory, setActiveCategory] = React.useState('all');

  const colors = getColors(activeClient);
  const logoUrl = getLogo(resolvedLogoUrl);
  const templates = buildTemplates(colors, logoUrl);

  const filtered = activeCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category === activeCategory);

  return (
    <div className="space-y-3" data-testid="template-gallery">
      <label className="text-xs font-bold uppercase tracking-wider text-[#64748b] block">
        Templates for {activeClient?.name || 'Client'}
      </label>

      {/* Category filter */}
      <div className="flex gap-1 flex-wrap">
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md transition-colors ${
              activeCategory === cat.id
                ? 'text-white'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
            style={activeCategory === cat.id ? { backgroundColor: 'var(--brand-primary)' } : {}}
            data-testid={`template-cat-${cat.id}`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {filtered.map((template) => {
          const Icon = template.icon;
          return (
            <button
              key={template.id}
              onClick={() => onSelectTemplate(template)}
              className="group p-3 rounded-xl border border-[#e2e8f0] hover:shadow-lg transition-all text-left"
              style={{ '--hover-border': 'var(--brand-accent)' }}
              data-testid={`template-${template.id}`}
            >
              <div 
                className="w-full h-16 rounded-lg mb-2 flex items-center justify-center"
                style={{ backgroundColor: template.preview.bg }}
              >
                <Icon className="w-6 h-6" style={{ color: template.preview.accent }} />
              </div>
              <span className="text-xs font-semibold text-[#1e293b]">{template.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Need to keep a way to import TEMPLATES for backward compat
export const TEMPLATES = [];
